import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useIdle } from './IdleContext';

export type PlayerMode = 'audio' | 'youtube';

interface LoadAudioOptions {
  startTime?: number;
  shouldPlay?: boolean;
}

interface AudioContextType {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isLoading: boolean;
  audioUrl: string | null;
  volume: number;
  playbackRate: number;
  playerMode: PlayerMode;
  mediaSeekRequestId: number;
  mediaSeekRequestTime: number | null;
  loadAudio: (url: string, options?: LoadAudioOptions) => void;
  play: () => Promise<void>;
  pause: () => void;
  seekTo: (time: number) => void;
  playSegment: (startTime: number, endTime?: number) => Promise<void>;
  setVolume: (volume: number) => void;
  setPlaybackRate: (rate: number) => void;
  // YouTube mode methods
  setPlayerMode: (mode: PlayerMode) => void;
  setYouTubeTime: (time: number) => void;
  setYouTubeDuration: (duration: number) => void;
  setYouTubePlaying: (playing: boolean) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

// Hook that only provides seekTo function without subscribing to currentTime updates
export const useAudioSeek = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudioSeek must be used within an AudioProvider');
  }
  return { seekTo: context.seekTo };
};

interface AudioProviderProps {
  children: React.ReactNode;
}

export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
  const { setMediaPlaying } = useIdle();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(1);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [playerMode, setPlayerModeState] = useState<PlayerMode>('audio');
  const [mediaSeekRequestId, setMediaSeekRequestId] = useState(0);
  const [mediaSeekRequestTime, setMediaSeekRequestTime] = useState<number | null>(null);
  const playerModeRef = useRef<PlayerMode>('audio'); // Ref for immediate access in callbacks
  const segmentEndTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const pendingSeekTimeRef = useRef<number | null>(null);
  const pendingAutoPlayRef = useRef(false);

  const resolveAudioUrl = useCallback((url: string) => {
    return url.startsWith('http') ? url : window.location.origin + url;
  }, []);

  const queueVideoSeek = useCallback((time: number) => {
    setMediaSeekRequestTime(time);
    setMediaSeekRequestId((previous) => previous + 1);
  }, []);

  // Sync playing state to IdleContext for CPU optimization
  useEffect(() => {
    setMediaPlaying(isPlaying);
  }, [isPlaying, setMediaPlaying]);

  // Ensure audio element is muted when in YouTube mode
  // This catches cases where setPlayerMode was called before audioRef was ready
  useEffect(() => {
    if (playerMode === 'youtube' && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.muted = true;
    } else if (playerMode === 'audio' && audioRef.current) {
      audioRef.current.muted = false;
    }
  }, [playerMode]);

  // YouTube mode methods - these are called by the YouTubePlayer component
  const setYouTubeTime = useCallback((time: number) => {
    if (playerMode === 'youtube') {
      setCurrentTime(time);
    }
  }, [playerMode]);

  const setYouTubeDuration = useCallback((newDuration: number) => {
    if (playerMode === 'youtube') {
      setDuration(newDuration);
    }
  }, [playerMode]);

  const setYouTubePlaying = useCallback((playing: boolean) => {
    if (playerMode === 'youtube') {
      setIsPlaying(playing);
    }
  }, [playerMode]);

  const setPlayerMode = useCallback((mode: PlayerMode) => {
    console.log('[AudioContext] Setting player mode:', mode);
    playerModeRef.current = mode; // Update ref immediately for callbacks
    setPlayerModeState(mode);

    // When switching to YouTube, pause and mute the audio element
    if (mode === 'youtube' && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.muted = true;
    }
    // When switching back to audio mode, unmute
    if (mode === 'audio' && audioRef.current) {
      audioRef.current.muted = false;
    }
  }, []);

  // Load audio from URL
  const loadAudio = useCallback((url: string, options: LoadAudioOptions = {}) => {
    // Skip loading audio if in YouTube mode - video player handles audio
    if (playerModeRef.current === 'youtube') {
      console.log('[AudioContext] Skipping audio load - in YouTube mode');
      return;
    }

    pendingSeekTimeRef.current = Number.isFinite(options.startTime)
      ? Math.max(0, options.startTime as number)
      : null;
    pendingAutoPlayRef.current = !!options.shouldPlay;

    // Determine the full URL (handle both relative and absolute URLs)
    const fullUrl = resolveAudioUrl(url);
    console.log('[AudioContext] Loading audio from URL:', url);
    console.log('[AudioContext] Full URL:', fullUrl);
    console.log('[AudioContext] Audio element exists:', !!audioRef.current);
    setAudioUrl(url);
    
    if (audioRef.current) {
      const audio = audioRef.current;
      const currentSrc = audio.currentSrc || audio.src || '';
      const sameSource = currentSrc === fullUrl;

      if (sameSource) {
        console.log('[AudioContext] Reusing already-loaded audio element for URL:', url);

        if (pendingSeekTimeRef.current !== null) {
          const targetTime = Number.isFinite(audio.duration) && audio.duration > 0
            ? Math.min(pendingSeekTimeRef.current, audio.duration)
            : pendingSeekTimeRef.current;
          if (Math.abs(audio.currentTime - targetTime) > 0.05) {
            audio.currentTime = targetTime;
          }
          setCurrentTime(targetTime);
          lastUpdateTimeRef.current = Date.now();
          pendingSeekTimeRef.current = null;
        }

        const hasBufferedAudio = audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;
        setIsLoading(!hasBufferedAudio);

        if (options.shouldPlay) {
          if (audio.paused) {
            void audio.play()
              .then(() => setIsPlaying(true))
              .catch((error) => console.error('[AudioContext] Error playing reused audio:', error));
          } else {
            setIsPlaying(true);
          }
        } else if (options.shouldPlay === false && !audio.paused) {
          audio.pause();
          setIsPlaying(false);
        }

        return;
      }

      setIsLoading(true);
      setIsPlaying(false);

      // Add event listeners for load progress
      const handleLoadStart = () => {
        console.log('[AudioContext] Audio load started for URL:', url);
      };
      
      const handleProgress = () => {
        console.log('[AudioContext] Audio loading progress');
      };
      
      const handleCanPlay = () => {
        console.log('[AudioContext] Audio can start playing');
      };
      
      const handleCanPlayThrough = () => {
        console.log('[AudioContext] Audio can play through without buffering');
      };
      
      const handleLoadedData = () => {
        console.log('[AudioContext] Audio loaded data event fired');
      };
      
      // Add temporary event listeners
      audio.addEventListener('loadstart', handleLoadStart, { once: true });
      audio.addEventListener('progress', handleProgress, { once: true });
      audio.addEventListener('canplay', handleCanPlay, { once: true });
      audio.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
      audio.addEventListener('loadeddata', handleLoadedData, { once: true });
      
      console.log('[AudioContext] Setting audio src to:', url);
      audio.src = url;
      audio.load();
      console.log('[AudioContext] Called load() on audio element');
    } else {
      console.error('[AudioContext] Audio ref is not available');
    }
  }, [resolveAudioUrl]);

  // Play audio
  const play = useCallback(async () => {
    // In YouTube mode, don't play the HTML audio element but still set isPlaying
    // so the YouTubePlayer component can respond to the state change
    // Use ref for immediate value (state may be stale in callback)
    if (playerModeRef.current === 'youtube') {
      console.log('[AudioContext] YouTube mode - setting isPlaying true for YouTube player');
      setIsPlaying(true);
      return;
    }

    if (!audioRef.current) return;

    try {
      console.log('[AudioContext] Playing audio');
      console.log('[AudioContext] Audio element state:', {
        src: audioRef.current.src,
        readyState: audioRef.current.readyState,
        networkState: audioRef.current.networkState,
        error: audioRef.current.error,
        duration: audioRef.current.duration,
        paused: audioRef.current.paused
      });
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('[AudioContext] Error playing audio:', error);
      if (error instanceof Error) {
        console.error('[AudioContext] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
    }
  }, []); // No dependencies needed since we use ref

  // Pause audio
  const pause = useCallback(() => {
    // In YouTube mode, just set isPlaying false for YouTubePlayer to respond
    if (playerModeRef.current === 'youtube') {
      console.log('[AudioContext] YouTube mode - setting isPlaying false for YouTube player');
      setIsPlaying(false);
      return;
    }

    if (!audioRef.current) return;

    // console.log('[AudioContext] Pausing audio');
    audioRef.current.pause();
    setIsPlaying(false);
    segmentEndTimeRef.current = null;
  }, []);

  // Seek to specific time
  const seekTo = useCallback((time: number) => {
    console.log('[AudioContext] Seeking to time:', time, 'playerMode:', playerModeRef.current);

    // Always update currentTime state (for YouTube player to receive)
    setCurrentTime(time);
    lastUpdateTimeRef.current = Date.now();
    queueVideoSeek(time);

    // In YouTube mode, don't touch the HTML audio element
    if (playerModeRef.current === 'youtube') {
      return;
    }

    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
  }, [queueVideoSeek]); // No dependency on player mode needed since we use ref

  // Play a specific segment
  const playSegment = useCallback(async (startTime: number, endTime?: number) => {
    console.log('[AudioContext] Playing segment:', { startTime, endTime, playerMode: playerModeRef.current });
    queueVideoSeek(startTime);

    // In YouTube mode, update currentTime and trigger play
    if (playerModeRef.current === 'youtube') {
      setCurrentTime(startTime);
      setIsPlaying(true); // Also trigger play in YouTube mode
      lastUpdateTimeRef.current = Date.now();
      return;
    }

    if (!audioRef.current) return;

    // Seek to start time
    audioRef.current.currentTime = startTime;
    segmentEndTimeRef.current = endTime || null;

    // Start playing
    await play();
  }, [play, queueVideoSeek]); // Only depends on play helper and video seek helper

  // Set volume
  const setVolume = useCallback((newVolume: number) => {
    if (!audioRef.current) return;
    
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    console.log('[AudioContext] Setting volume:', clampedVolume);
    
    audioRef.current.volume = clampedVolume;
    setVolumeState(clampedVolume);
  }, []);

  // Set playback rate
  const setPlaybackRate = useCallback((rate: number) => {
    if (!audioRef.current) return;
    
    console.log('[AudioContext] Setting playback rate:', rate);
    audioRef.current.playbackRate = rate;
    setPlaybackRateState(rate);
  }, []);

  // Handle time update
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      // Skip time updates from HTML audio when in YouTube mode
      // YouTube player controls the time in that mode
      if (playerModeRef.current === 'youtube') {
        return;
      }

      const newTime = audio.currentTime;

      // Only update state every 100ms to reduce re-renders
      const now = Date.now();
      if (now - lastUpdateTimeRef.current >= 100) {
        setCurrentTime(newTime);
        lastUpdateTimeRef.current = now;
      }

      // Check if we've reached the segment end time
      if (segmentEndTimeRef.current && newTime >= segmentEndTimeRef.current) {
        console.log('[AudioContext] Reached segment end time');
        pause();
      }
    };

    const handleLoadedMetadata = () => {
      console.log('[AudioContext] Audio metadata loaded, duration:', audio.duration);
      setDuration(audio.duration);

      if (pendingSeekTimeRef.current !== null) {
        const targetTime = Number.isFinite(audio.duration) && audio.duration > 0
          ? Math.min(pendingSeekTimeRef.current, audio.duration)
          : pendingSeekTimeRef.current;
        audio.currentTime = targetTime;
        setCurrentTime(targetTime);
        lastUpdateTimeRef.current = Date.now();
        pendingSeekTimeRef.current = null;
      }

      setIsLoading(false);

      if (pendingAutoPlayRef.current) {
        pendingAutoPlayRef.current = false;
        void audio.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.error('[AudioContext] Error resuming audio after reload:', error);
          });
      }
    };

    const handleEnded = () => {
      console.log('[AudioContext] Audio ended');
      setIsPlaying(false);
      segmentEndTimeRef.current = null;
    };

    const handleError = (e: Event) => {
      const audio = e.target as HTMLAudioElement;
      const error = audio.error;
      pendingSeekTimeRef.current = null;
      pendingAutoPlayRef.current = false;
      
      console.error('[AudioContext] Audio error:', {
        event: e,
        error: error,
        code: error?.code,
        message: error?.message,
        src: audio.src,
        networkState: audio.networkState,
        readyState: audio.readyState,
        NETWORK_STATES: {
          NETWORK_EMPTY: 0,
          NETWORK_IDLE: 1,
          NETWORK_LOADING: 2,
          NETWORK_NO_SOURCE: 3,
          currentState: audio.networkState
        },
        READY_STATES: {
          HAVE_NOTHING: 0,
          HAVE_METADATA: 1,
          HAVE_CURRENT_DATA: 2,
          HAVE_FUTURE_DATA: 3,
          HAVE_ENOUGH_DATA: 4,
          currentState: audio.readyState
        }
      });
      
      // Log specific error types
      if (error) {
        switch (error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            console.error('[AudioContext] The fetching of the audio was aborted by the user');
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            console.error('[AudioContext] A network error occurred while fetching the audio');
            break;
          case MediaError.MEDIA_ERR_DECODE:
            console.error('[AudioContext] An error occurred while decoding the audio');
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            console.error('[AudioContext] The audio format is not supported');
            break;
          default:
            console.error('[AudioContext] An unknown error occurred');
        }
      }
      
      setIsLoading(false);
      setIsPlaying(false);
    };

    const handlePlay = () => {
      // console.log('[AudioContext] Audio play event');
      setIsPlaying(true);
    };

    const handlePause = () => {
      // console.log('[AudioContext] Audio pause event');
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [pause]); // Using ref for playerMode so no dependency needed

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  const value: AudioContextType = {
    audioRef,
    currentTime,
    duration,
    isPlaying,
    isLoading,
    audioUrl,
    volume,
    playbackRate,
    playerMode,
    mediaSeekRequestId,
    mediaSeekRequestTime,
    loadAudio,
    play,
    pause,
    seekTo,
    playSegment,
    setVolume,
    setPlaybackRate,
    setPlayerMode,
    setYouTubeTime,
    setYouTubeDuration,
    setYouTubePlaying,
  };

  return (
    <AudioContext.Provider value={value}>
      <audio
        ref={audioRef}
        preload="auto"
        style={{ display: 'none' }}
      />
      {children}
    </AudioContext.Provider>
  );
};
